import {
  Box,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { RiDashboardLine, RiLogoutBoxRLine, RiSettings4Line } from "react-icons/ri";
import { FaBook, FaUserCircle } from "react-icons/fa";
import { MdHomeWork, MdQuiz, MdSchedule } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import StudentWrapper from "../Auth/StudentWrapper";
import ThemeToggleButton from "../dashboard/ThemeToggleButton";
import { logoutUserAction } from "../../modules/auth/Actions";
import { brandPalette } from "../../constants/brandPalette";
import {
  adminFooterAvatarBoxProps,
  adminFooterAvatarIconProps,
  adminFooterControlProps,
  adminFooterNameProps,
  adminFooterProps,
  adminFooterSettingsButtonProps,
  adminHeaderSubtitleProps,
  adminNavItemProps,
  adminSectionLabelProps,
  adminSidebarColors,
  adminSidebarLogoProps,
  adminSidebarPopoverProps,
  adminSidebarShellProps,
} from "../../constants/adminSidebarTheme";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";
import { useAdminTheme } from "../../context/AdminThemeContext";

/**
 * Student area: desktop fixed sidebar (md+); mobile compact top bar + full-width content below.
 */
const StudentPortalShell = ({ children }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const userData = useSelector((state) => state.user.userData);
  const { themeMode } = useAdminTheme();
  const isDark = themeMode === "dark";
  const footerControls = adminFooterControlProps(isDark);
  const popoverChrome = adminSidebarPopoverProps(isDark);

  const isHome = pathname === "/students" || pathname === "/home";
  const isCourses = pathname.startsWith("/students/courses");
  const isOfficeHours = pathname.startsWith("/students/office-hours");
  const isStudentExams =
    pathname.startsWith("/students/exams") || pathname === "/student/exams";
  const isQuickLinks = pathname.startsWith("/students/quick-links");
  const isQuickLinksHomework = isQuickLinks && !search.includes("tab=exams");
  const isQuickLinksExams = isQuickLinks && search.includes("tab=exams");

  const handleLogout = () => {
    dispatch(logoutUserAction());
  };

  const accountMenuListProps = {
    bg: "var(--surface-raised)",
    borderWidth: "1px",
    borderColor: popoverChrome.borderColor,
    borderRadius: "md",
    py: 1,
    minW: "10rem",
    boxShadow: "lg",
    zIndex: 50,
  };

  const accountMenuItemProps = {
    bg: "transparent",
    color: isDark ? brandPalette.cream : adminSidebarColors.inactiveText,
    fontWeight: "600",
    fontSize: "sm",
    py: 2.5,
    px: 3,
    iconSpacing: 3,
    _hover: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
    _focus: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
    _active: {
      bg: isDark ? brandPalette.secondary : "rgba(91, 13, 22, 0.08)",
      color: isDark ? "white" : brandPalette.primary,
    },
  };

  const mobileLinkColor = (active) =>
    active
      ? brandPalette.primary
      : isDark
        ? "var(--text-muted)"
        : adminSidebarColors.inactiveText;

  const SidebarNav = (
    <>
      <Box px={6} py={8}>
        <Image
          {...adminSidebarLogoProps(isDark)}
          onClick={() => window.location.replace("/students")}
        />
        <Text {...adminHeaderSubtitleProps(isDark)} mt={4} textAlign="center">
          Academic Portal
        </Text>
      </Box>

      <VStack as="nav" align="stretch" spacing={1} px={4} flex="1" pt={2}>
        <Link to="/students" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isHome, isDark)}>
            <Box as={RiDashboardLine} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Home
            </Text>
          </Flex>
        </Link>
        <Link to="/students/courses" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isCourses, isDark)}>
            <Box as={FaBook} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Courses
            </Text>
          </Flex>
        </Link>
        <Link to="/students/office-hours" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isOfficeHours, isDark)}>
            <Box as={MdSchedule} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Office hour
            </Text>
          </Flex>
        </Link>

        <Text {...adminSectionLabelProps(isDark)} mt={4} px={2}>
          Quick Links
        </Text>
        <Link to="/students/quick-links?tab=homework" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isQuickLinksHomework, isDark)}>
            <Box as={MdHomeWork} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Homework
            </Text>
          </Flex>
        </Link>
        <Link to="/students/quick-links?tab=exams" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isQuickLinksExams, isDark)}>
            <Box as={MdQuiz} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Exams
            </Text>
          </Flex>
        </Link>

        <Link to="/students/exams" style={{ textDecoration: "none" }}>
          <Flex {...adminNavItemProps(isStudentExams, isDark)}>
            <Box as={MdQuiz} fontSize="18px" />
            <Text flex="1" textAlign="left">
              Student Exams
            </Text>
          </Flex>
        </Link>
      </VStack>

      <Flex {...adminFooterProps(isDark)} align="center" gap={3} justify="space-between">
        <Flex align="center" gap={3} minW={0} flex="1">
          <Flex {...adminFooterAvatarBoxProps(isDark)}>
            <Box as={FaUserCircle} {...adminFooterAvatarIconProps(isDark)} />
          </Flex>
          <Box overflow="hidden">
            <Text {...adminFooterNameProps(isDark)}>{userData?.name || "Student"}</Text>
          </Box>
        </Flex>

        <ThemeToggleButton {...footerControls.themeToggle} />
        <Menu placement="top-end">
          <MenuButton
            as={IconButton}
            aria-label="Account and logout"
            icon={<RiSettings4Line size={22} />}
            {...adminFooterSettingsButtonProps(isDark)}
          />
          <MenuList {...accountMenuListProps}>
            <MenuItem
              {...accountMenuItemProps}
              icon={<RiLogoutBoxRLine size={18} />}
              onClick={handleLogout}
            >
              Log out
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </>
  );

  return (
    <StudentWrapper roles={["Student"]}>
      <Box minH="100vh" bg={studentSurfaces.canvas} dir="ltr" position="relative" w="100%" overflowX="hidden">
        {/* Desktop: fixed sidebar (out of document flow for width) */}
        <Flex
          display={{ base: "none", md: "flex" }}
          position="fixed"
          left={0}
          top={0}
          zIndex={30}
          w="260px"
          h="100vh"
          {...adminSidebarShellProps(isDark)}
          direction="column"
        >
          {SidebarNav}
        </Flex>

        {/* Mobile: single full-width row — do NOT sit beside main in a horizontal flex */}
        <Flex
          display={{ base: "flex", md: "none" }}
          position="sticky"
          top={0}
          zIndex={40}
          w="100%"
          maxW="100vw"
          bg="var(--surface-raised)"
          px={3}
          py={2.5}
          align="center"
          justify="space-between"
          gap={2}
          borderBottomWidth="1px"
          borderColor="var(--border-subtle)"
          flexShrink={0}
          boxShadow="0 2px 12px rgba(91, 13, 22, 0.06)"
        >
          <Text
            fontWeight="800"
            color={isDark ? brandPalette.cream : brandPalette.primary}
            fontSize="sm"
            noOfLines={1}
            flexShrink={0}
          >
            Edutopia
          </Text>
          <Flex align="center" gap={2} flex="1" justify="flex-end" minW={0}>
            <Text
              as={Link}
              to="/students"
              fontSize="xs"
              fontWeight={isHome ? "800" : "600"}
              color={mobileLinkColor(isHome)}
              whiteSpace="nowrap"
            >
              Home
            </Text>
            <Text
              as={Link}
              to="/students/courses"
              fontSize="xs"
              fontWeight={isCourses ? "800" : "600"}
              color={mobileLinkColor(isCourses)}
              whiteSpace="nowrap"
            >
              Courses
            </Text>
            <Text
              as={Link}
              to="/students/office-hours"
              fontSize="xs"
              fontWeight={isOfficeHours ? "800" : "600"}
              color={mobileLinkColor(isOfficeHours)}
              whiteSpace="nowrap"
            >
              Office
            </Text>
            <Text
              as={Link}
              to="/students/quick-links?tab=homework"
              fontSize="xs"
              fontWeight={isQuickLinksHomework ? "800" : "600"}
              color={mobileLinkColor(isQuickLinksHomework)}
              whiteSpace="nowrap"
            >
              HW
            </Text>
            <Text
              as={Link}
              to="/students/quick-links?tab=exams"
              fontSize="xs"
              fontWeight={isQuickLinksExams ? "800" : "600"}
              color={mobileLinkColor(isQuickLinksExams)}
              whiteSpace="nowrap"
            >
              QExams
            </Text>
            <Text
              as={Link}
              to="/students/exams"
              fontSize="xs"
              fontWeight={isStudentExams ? "800" : "600"}
              color={mobileLinkColor(isStudentExams)}
              whiteSpace="nowrap"
            >
              Exams
            </Text>
            <ThemeToggleButton tone="onSurface" size="sm" iconSize={18} />
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Settings"
                icon={<RiSettings4Line />}
                variant="ghost"
                color={isDark ? brandPalette.cream : brandPalette.primary}
                size="sm"
                flexShrink={0}
                borderRadius="12px"
                _hover={{ bg: isDark ? "rgba(255,255,255,0.12)" : "rgba(91, 13, 22, 0.08)" }}
              />
              <MenuList {...accountMenuListProps}>
                <MenuItem
                  {...accountMenuItemProps}
                  icon={<RiLogoutBoxRLine size={18} />}
                  onClick={handleLogout}
                >
                  Log out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Flex>

        {/* Main: offset for fixed sidebar; width must not be 100% + margin or content overflows */}
        <Box
          w={{ base: "100%", md: "calc(100% - 260px)" }}
          maxW={{ base: "100%", md: "calc(100% - 260px)" }}
          minW={0}
          ml={{ base: 0, md: "260px" }}
          minH="100vh"
        >
          {children}
        </Box>
      </Box>
    </StudentWrapper>
  );
};

export default StudentPortalShell;
